import { clinicAuthClient } from "@auth/clinicAuthClient";
import { useSessionStore } from "@auth/sessionStore";
import { router } from "@components/AppShell";
import { UserContext, type UserContextValue } from "@context/UserContext";
import { flushPendingRecordings } from "@transcription/recordingsClient";
import { userClient, type ClinicUser } from "@users/userClient";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const userSession = useSessionStore((s) => s.user);
  const isUserAuthed = userSession !== null;

  const [currentUser, setCurrentUser] = useState<ClinicUser | null>(null);

  // Clear the resolved user the instant the session goes away — during render
  // (not in an effect) so the header never flashes a stale name.
  const [prevAuthed, setPrevAuthed] = useState(isUserAuthed);
  if (prevAuthed !== isUserAuthed) {
    setPrevAuthed(isUserAuthed);
    if (!isUserAuthed) setCurrentUser(null);
  }

  // Once a user-scope session exists, resolve who it is (for the header) and
  // flush any recordings captured before sign-in.
  useEffect(() => {
    if (!isUserAuthed) return;
    flushPendingRecordings().catch(() => {});
    let cancelled = false;
    userClient
      .getCurrent()
      .then((u) => {
        if (!cancelled) setCurrentUser(u);
      })
      .catch(() => {
        if (!cancelled) setCurrentUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isUserAuthed]);

  const submitAccessCode = useCallback(async (code: string, pin: string) => {
    const next = await clinicAuthClient.selectUser(code, pin);
    useSessionStore.getState().setUser(next);
  }, []);

  const switchUser = useCallback(() => {
    const user = useSessionStore.getState().user;
    // Reset to home before tearing down the session so the next user starts
    // fresh instead of restoring this user's page. replace() keeps the old page
    // out of the back-stack (shared clinic device).
    router.history.replace("/");
    useSessionStore.getState().setUser(null);
    if (user) void clinicAuthClient.logout(user.refreshToken);
  }, []);

  const value = useMemo<UserContextValue>(
    () => ({
      session: userSession,
      currentUser,
      isUserAuthed,
      submitAccessCode,
      switchUser,
    }),
    [userSession, currentUser, isUserAuthed, submitAccessCode, switchUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
